import { SEPLAT_BRAND, SEPLAT_BRAND_COLORS } from "@sga/shared";
import type { DraftSaveRequest } from "@sga/shared";
import { AvailabilityPanel } from "./AvailabilityPanel.jsx";
import { MarkupEditor } from "./MarkupEditor.jsx";
import { FormBehaviourPanel } from "./FormBehaviourPanel.jsx";

/**
 * A skeleton, not a notice. Every bracket is a decision only the programme owner can make, and it
 * is written so that publishing it unedited is obviously wrong to anyone who reads the page —
 * which is the point: a plausible-looking notice nobody checked is worse than none.
 */
const PRIVACY_NOTICE_TEMPLATE = `**Who is collecting this data**

[Organisation name] ("we") collects the information on this form to administer the [programme name] programme.

**What we collect and why**

- Your name, contact details and the answers you give on this form, so we can assess your application and contact you about it.
- [Add anything else this form asks for — bank details, identity numbers, documents — and why.]

**Who we share it with**

[Name the parties: programme partners, the JV, any processor. Say "we do not share it with anyone else" if that is true.]

**How long we keep it**

[State a period, and what happens at the end of it.]

**Your rights**

Under the Nigeria Data Protection Act 2023 you may ask to see the data we hold about you, have it corrected, or have it deleted. To do so, or to raise a concern, contact our Data Protection Officer at [DPO name and email address].`;

const labelClass =
  "mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
const inputClass = "w-full rounded-md border px-2 py-1.5 text-sm";

/**
 * The Settings tab — availability, behaviour flags, dedupe rules, notifications and the privacy
 * notice (docs/04-BUILD-PLAN.md Phase 4).
 *
 * Only the privacy notice actually belongs to the draft schema. Availability, behaviour and
 * notifications are columns on the `Form` row, and each is edited by a panel that owns its own
 * endpoint — which is the correction this file needed: the behaviour flags used to be checkboxes
 * writing into the draft's `settings` blob, a value snapshotted at publish and read back here but
 * never consulted at runtime. They changed nothing, and read back from the blob rather than the
 * row, so an absent key rendered as ticked.
 */
export const SettingsTab = ({
  schema,
  formId,
  onChange,
}: {
  schema: DraftSaveRequest;
  formId: string;
  onChange: (next: DraftSaveRequest) => void;
}): JSX.Element => {
  return (
    <div className="max-w-2xl space-y-6 p-4">
      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Availability</h3>
        <AvailabilityPanel formId={formId} />
      </section>

      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Privacy notice</h3>
        <p className="mb-2 text-xs text-muted-foreground">
          Required before publishing. This is an NDPA 2023 obligation, not a preference — the
          publish endpoint rejects a form without one.
        </p>
        <label htmlFor="privacy-notice" className={labelClass}>
          Notice shown to applicants
        </label>
        <MarkupEditor
          id="privacy-notice"
          rows={12}
          value={schema.privacyNotice ?? ""}
          onChange={(next) => onChange({ ...schema, privacyNotice: next })}
          placeholder="Explain what data you collect, why, how long you keep it, and who the DPO is."
        />
        {!schema.privacyNotice?.trim() && (
          <button
            type="button"
            onClick={() => onChange({ ...schema, privacyNotice: PRIVACY_NOTICE_TEMPLATE })}
            className="mt-2 rounded-md border px-2 py-1 text-xs hover:bg-secondary"
          >
            Start from a template
          </button>
        )}
      </section>

      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Behaviour</h3>
        <FormBehaviourPanel formId={formId} />
      </section>

      <section className="rounded-md border border-dashed p-3">
        <h3 className="mb-1 font-heading text-sm font-semibold">Notifications and webhooks</h3>
        <p className="text-xs text-muted-foreground">
          Configured in their own tabs above, not here — each has its own screen with delivery
          history and a replay/test-send action that a shared settings tab has no room for.
        </p>
      </section>

      <section className="rounded-md border border-dashed p-3">
        <h3 className="mb-1 font-heading text-sm font-semibold">Duplicate prevention</h3>
        <p className="text-xs text-muted-foreground">
          Configured in the <strong>Duplicates</strong> tab above, not here. Per-field "Use for
          duplicate detection" (Properties panel) marks which answers matter for matching; the rules
          themselves — and the database constraint that actually blocks a duplicate at submit — live
          on their own screen, which also reports how many duplicates a new rule found in what the
          form already holds.
        </p>
      </section>
    </div>
  );
};

/**
 * The Design tab — brand profile, logo layout, colour tokens (docs/04-BUILD-PLAN.md Phase 4).
 *
 * The colour swatches are the real Seplat palette read from seplatenergy.com; see
 * packages/shared/src/branding/tokens.ts for provenance.
 */
export const DesignTab = ({
  schema,
  onChange,
}: {
  schema: DraftSaveRequest;
  onChange: (next: DraftSaveRequest) => void;
}): JSX.Element => {
  const settings = schema.settings as Record<string, unknown>;
  const setSetting = (key: string, value: unknown): void =>
    onChange({ ...schema, settings: { ...settings, [key]: value } });

  return (
    <div className="max-w-2xl space-y-6 p-4">
      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Logos</h3>
        <label htmlFor="logo-layout" className={labelClass}>
          Logo layout
        </label>
        <select
          id="logo-layout"
          className={inputClass}
          value={(settings.logoLayout as string) ?? "side_by_side"}
          onChange={(event) => setSetting("logoLayout", event.target.value)}
        >
          <option value="side_by_side">Side by side (NNPC left, Seplat right)</option>
          <option value="stacked">Stacked</option>
          <option value="primary_only">Seplat only</option>
          <option value="secondary_only">NNPC only</option>
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          Every form inherits the JV brand profile with both marks side by side unless changed here.
        </p>
      </section>

      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Layout style</h3>
        <select
          className={inputClass}
          aria-label="Layout style"
          value={(settings.layoutStyle as string) ?? "card"}
          onChange={(event) => setSetting("layoutStyle", event.target.value)}
        >
          <option value="card">Card</option>
          <option value="full_width">Full width</option>
          <option value="conversational">One question per page</option>
        </select>
      </section>

      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Brand colours</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Read from Seplat Energy&rsquo;s own stylesheet. Corporate Communications signs these off
          before go-live.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(SEPLAT_BRAND_COLORS).map(([token, hex]) => (
            <div key={token} className="flex items-center gap-2 rounded-md border p-2">
              <span
                className="h-6 w-6 shrink-0 rounded border"
                style={{ background: hex }}
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium">{token}</span>
                <code className="text-[11px] text-muted-foreground">{hex}</code>
              </span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-2 font-heading text-sm font-semibold">Full palette</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SEPLAT_BRAND).map(([token, hex]) => (
            <div key={token} className="text-center">
              <span
                className="block h-10 w-16 rounded border"
                style={{ background: hex }}
                aria-label={`${token} ${hex}`}
              />
              <code className="text-[10px] text-muted-foreground">{token}</code>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
